import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPayment, updatePayment } from "../../../data";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import {
  downloadAllInvoices,
  sendReminderInvoiceMails,
  getReminderMailPreview,
} from "@/data/requests/payment-requests";
import {
  Receipt,
  FileText,
  MapPin,
  Tag,
  DollarSign,
  Mail,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Download,
  Send,
  User,
  Clock,
  Eye,
  Loader2,
  Phone,
  Pencil,
} from "lucide-react";
import ReactQuill from "react-quill";
import AuthStore from "@/stores/AuthStore";
import { useState } from "react";
import { getUsers } from "@/data/requests/user-requests";
import { filterRelatedUsers } from "@/utils/related-user-options";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatTRY } from "@/utils/currency";

export function PaymentDetailScreen() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["payment", id],
    queryFn: async () => await getPayment(id),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const emptyEditForm = {
    name: "",
    invoiceId: "",
    country: "",
    relatedUser: "",
    minAcceptedInvoiceDate: "",
    mailBodyMessage: "",
    managerName: "",
    managerEmail: "",
    managerPhone: "",
  };
  const [editForm, setEditForm] = useState(emptyEditForm);
  const setEditField = (key) => (value) =>
    setEditForm((prev) => ({ ...prev, [key]: value }));

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
    enabled: editDialogOpen,
  });

  const editMutation = useMutation({
    mutationFn: async (values) => await updatePayment({ id, data: values }),
    onSuccess: () => {
      setEditDialogOpen(false);
      toast({ title: "Ödeme bilgileri güncellendi", type: "success" });
      queryClient.invalidateQueries({ queryKey: ["payment", id] });
    },
    onError: (error) => {
      toast({
        title: "Güncelleme başarısız",
        description:
          error.response?.data?.error || error.message || "Bir hata oluştu",
        type: "error",
      });
    },
  });

  const formatDateTr = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getUTCFullYear()}`;
  };

  // Dialog acilirken mevcut degerleri forma doldur
  const handleEditDialogOpen = (open) => {
    if (open) {
      const p = data?.data || {};
      const firstCommission = p.commissions?.[0] || {};
      setEditForm({
        name: p.name || "",
        invoiceId: p.invoiceId || "",
        country: firstCommission.country || "",
        relatedUser: p.relatedUser || "",
        minAcceptedInvoiceDate: formatDateTr(firstCommission.minAcceptedInvoiceDate),
        mailBodyMessage: p.mailBodyMessage || "",
        managerName: p.managerName || "",
        managerEmail: p.managerEmail || "",
        managerPhone: p.managerPhone || "",
      });
    }
    setEditDialogOpen(open);
  };

  const submitEdit = () => {
    if (!editForm.name.trim()) {
      toast({ title: "Ad boş olamaz", type: "error" });
      return;
    }
    if (!editForm.invoiceId.trim()) {
      toast({ title: "Fatura No boş olamaz", type: "error" });
      return;
    }
    const dateValue = editForm.minAcceptedInvoiceDate.trim();
    if (
      dateValue &&
      !/^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/.test(dateValue)
    ) {
      toast({ title: "Tarih gg.aa.yyyy formatında olmalı", type: "error" });
      return;
    }
    editMutation.mutate({
      ...editForm,
      minAcceptedInvoiceDate: dateValue,
    });
  };

  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const loadReminderPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const response = await getReminderMailPreview(id);
      setReminderPreview(response.data);
    } catch (error) {
      console.error("Error loading reminder preview:", error);
      toast({
        title: "Önizleme yüklenirken hata oluştu",
        message: error.message,
        type: "error",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleReminderDialogOpen = (open) => {
    setReminderDialogOpen(open);
    if (open) {
      loadReminderPreview();
    } else {
      setReminderPreview(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEmailTypeLabel = (type) => {
    const labels = {
      INVOICE_REQUEST: "Fatura Talebi",
      REMINDER: "Hatırlatma",
      CHANGE_REQUEST: "Değişiklik Talebi",
      CONFIRMATION: "Onay",
      CUSTOM: "Özel",
    };
    return labels[type] || type;
  };

  const mutationReminder = useMutation({
    mutationFn: async (data) => {
      await sendReminderInvoiceMails(data);
    },
    onSuccess: () => {
      toast({
        title: "Hatırlatma mailleri başarıyla gönderildi",
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["payment", id] });
      setReminderDialogOpen(false);
      setReminderPreview(null);
    },
    onError: (error) => {
      toast({
        title: "Mail gönderimi başarısız",
        message: error.message,
        type: "error",
      });
    },
  });

  const pageSettings = {
    title: "Ödeme Detayı",
    actions: [
      {
        name: "Listeye Dön",
        color: "primary",
        icon: "add-icon",
        onClick: () => {
          navigate("/payments");
        },
      },
      {
        name: "Tüm PDF'leri İndir",
        color: "primary",
        icon: "add-icon",
        onClick: async () => {
          await downloadAllInvoices(id).then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "invoices.zip");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
          });
        },
      },
    ],
  };

  const onSubmit = async () => {
    if (!reminderPreview || reminderPreview.totalCommissionsToRemind === 0) {
      toast({
        title: "Hatırlatma gönderilecek fatura yok",
        message: "Mail Sended durumunda fatura bulunamadı",
        type: "error",
      });
      return;
    }

    mutationReminder.mutate({
      paymentId: id,
      data: {
        mailBodyMessage: getValues("mailBodyMessage")?.trim(),
      },
    });
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div>Yükleniyor...</div>
      </AppLayout>
    );
  }

  if (mutationReminder.isPending) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Mail className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-lg font-medium">Mailler gönderiliyor...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statsCards = [
    {
      label: "Komisyon Sayısı",
      value: data.data.commissionCount,
      icon: Users,
      color: "blue",
      link: `/payments/${id}/commissions`,
      linkText: "Komisyonları Gör",
    },
    {
      label: "Gönderilen Mail Sayısı",
      value: data.data.sendedMailCount,
      icon: Mail,
      color: "green",
    },
    {
      label: "Yüklenen Faturalar",
      value: data.data.uploadedInvoiceCount,
      icon: FileText,
      color: "purple",
      link: `/payments/${id}/invoices`,
      linkText: "Fatura PDF'lerini Gör",
    },
    {
      label: "Onaylanan Faturalar",
      value: data.data.approvedInvoiceCount,
      icon: CheckCircle2,
      color: "emerald",
      link: `/payments/${id}/invoices`,
      linkText: "Fatura PDF'lerini Gör",
    },
    {
      label: "Muhasebe",
      value: data.data.accountingCount,
      icon: DollarSign,
      color: "cyan",
      link: `/payments/${id}/invoices`,
      linkText: "Fatura PDF'lerini Gör",
    },
    {
      label: "LUCA",
      value: data.data.lucaCount,
      icon: Receipt,
      color: "orange",
      link: `/payments/${id}/invoices`,
      linkText: "Fatura PDF'lerini Gör",
    },
    {
      label: "Ödendi",
      value: data.data.paidCount,
      icon: CheckCircle2,
      color: "green",
      link: `/payments/${id}/invoices`,
      linkText: "Fatura PDF'lerini Gör",
    },
    {
      label: "Hatalı Faturalar",
      value: data.data.errorInvoiceCount,
      icon: AlertCircle,
      color: "red",
      link: `/payments/${id}/commissions/error-commissions`,
      linkText: "Hatalı Komisyonlar",
    },
  ];

  const financialData = [
    {
      label: "Toplam Komisyon Tutarı",
      value: data.data.totalCommissionAmount,
      icon: DollarSign,
      color: "blue",
    },
    {
      label: "Toplam Ödenen Tutar",
      value: data.data.totalPaidAmount,
      icon: CheckCircle2,
      color: "green",
    },
    {
      label: "Toplam Luca Tutarı",
      value: data.data.totalLucaAmount,
      icon: Receipt,
      color: "orange",
    },
    {
      label: "Toplam Luca Hazır Tutarı",
      value: data.data.totalLucaReadyAmount,
      icon: TrendingUp,
      color: "emerald",
    },
    {
      label: "Toplam Bankadaki Tutar",
      value: data.data.totalInBankAmount,
      icon: DollarSign,
      color: "cyan",
    },
  ];

  const colorClasses = {
    blue: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
    green: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700",
    purple: "bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700",
    emerald: "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700",
    cyan: "bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700",
    orange: "bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700",
    red: "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700",
  };

  return (
    <AppLayout
      title={pageSettings.title + " - " + data.data.name}
      actions={pageSettings.actions}
      isLoading={isLoading}
    >
      <div className="px-4 py-6 space-y-6">
        {/* Üst kısım: Solda Payment Info, sağda Commission/istatistik kartları */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Payment Info Card - Sol */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Receipt className="h-5 w-5 text-green-600" />
                  Ödeme Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Receipt className="h-4 w-4" />
                      Ad
                    </Label>
                    <Input
                      value={data.data.name}
                      disabled
                      className="mt-1.5 font-medium"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      Fatura No
                    </Label>
                    <Input
                      value={data.data.invoiceId}
                      disabled
                      className="mt-1.5 font-mono"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      Ülke
                    </Label>
                    <Input
                      value={data.data.commissions[0]?.country || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Tag className="h-4 w-4" />
                      Ödeme Tipi
                    </Label>
                    <div className="mt-1.5">
                      <Badge
                        variant="outline"
                        className={
                          data.data.type === "FIXED"
                            ? colorClasses.purple
                            : colorClasses.blue
                        }
                      >
                        {data.data.type || "DEFAULT"}
                      </Badge>
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      İlgili Kullanıcı
                    </Label>
                    <Input
                      value={data.data.relatedUser || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>

                  {/* İstisna / gider pusulası formunda influencer'a gösterilir */}
                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      Proje Yöneticisi
                    </Label>
                    <Input
                      value={data.data.managerName || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      Proje Yöneticisi E-Posta
                    </Label>
                    <Input
                      value={data.data.managerEmail || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      Proje Yöneticisi Telefon
                    </Label>
                    <Input
                      value={data.data.managerPhone || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  {AuthStore.isHavePermission("PAYMENT_UPDATE") && (
                  <Dialog open={editDialogOpen} onOpenChange={handleEditDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full md:w-auto">
                        <Pencil className="mr-2 h-4 w-4" />
                        Ödemeyi Düzenle
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Ödeme Bilgilerini Düzenle</DialogTitle>
                        <DialogDescription>
                          Ülke ve en erken fatura tarihi bu ödemeye bağlı tüm
                          komisyonlara uygulanır.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="edit-name">Ad *</Label>
                            <Input
                              id="edit-name"
                              className="mt-1.5"
                              value={editForm.name}
                              onChange={(e) => setEditField("name")(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-invoiceId">Fatura No *</Label>
                            <Input
                              id="edit-invoiceId"
                              className="mt-1.5 font-mono"
                              value={editForm.invoiceId}
                              onChange={(e) => setEditField("invoiceId")(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-country">Ülke</Label>
                            <Input
                              id="edit-country"
                              className="mt-1.5"
                              value={editForm.country}
                              onChange={(e) => setEditField("country")(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-relatedUser">İlgili Kullanıcı</Label>
                            <Select
                              value={editForm.relatedUser || undefined}
                              onValueChange={setEditField("relatedUser")}
                            >
                              <SelectTrigger id="edit-relatedUser" className="mt-1.5">
                                <SelectValue placeholder="Kullanıcı seçin" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Sistem Kullanıcıları</SelectLabel>
                                  {filterRelatedUsers(usersData?.data).map((user) => (
                                    <SelectItem
                                      key={user.id}
                                      value={user.name || user.email}
                                    >
                                      {user.name} {user.email ? `(${user.email})` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-2">
                            <Label htmlFor="edit-minDate">
                              Kabul Edilen En Erken Fatura Tarihi
                            </Label>
                            <Input
                              id="edit-minDate"
                              className="mt-1.5"
                              placeholder="gg.aa.yyyy (örn. 15.06.2024)"
                              value={editForm.minAcceptedInvoiceDate}
                              onChange={(e) =>
                                setEditField("minAcceptedInvoiceDate")(e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Mail Mesajı</Label>
                          <ReactQuill
                            theme="snow"
                            className="mt-1.5"
                            value={editForm.mailBodyMessage}
                            onChange={setEditField("mailBodyMessage")}
                          />
                        </div>

                        <div>
                          <p className="text-sm font-medium">Proje Yöneticisi</p>
                          <p className="text-xs text-muted-foreground mb-2">
                            İstisna / gider pusulası ödeme bilgileri formunda
                            influencer'a gösterilir.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor="managerName">Adı Soyadı</Label>
                              <Input
                                id="managerName"
                                className="mt-1.5"
                                placeholder="Ad Soyad"
                                value={editForm.managerName}
                                onChange={(e) => setEditField("managerName")(e.target.value)}
                              />
                            </div>
                            <div>
                              <Label htmlFor="managerEmail">E-Posta</Label>
                              <Input
                                id="managerEmail"
                                type="email"
                                className="mt-1.5"
                                placeholder="ornek@newtvgeneration.com"
                                value={editForm.managerEmail}
                                onChange={(e) => setEditField("managerEmail")(e.target.value)}
                              />
                            </div>
                            <div>
                              <Label htmlFor="managerPhone">Telefon</Label>
                              <Input
                                id="managerPhone"
                                type="tel"
                                className="mt-1.5"
                                placeholder="+90 5.."
                                value={editForm.managerPhone}
                                onChange={(e) => setEditField("managerPhone")(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <DialogFooter>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditDialogOpen(false)}
                        >
                          İptal
                        </Button>
                        <Button
                          type="button"
                          disabled={editMutation.isPending}
                          onClick={submitEdit}
                        >
                          {editMutation.isPending ? "Kaydediliyor..." : "Kaydet"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  )}
                </div>

                <div className="mt-6">
                  <Dialog open={reminderDialogOpen} onOpenChange={handleReminderDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        className="w-full md:w-auto"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Hatırlatma Maili Gönder
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <DialogHeader>
                          <DialogTitle>Hatırlatma Maili Gönder</DialogTitle>
                          <DialogDescription>
                            {reminderPreview ? (
                              <span className="font-semibold text-primary">
                                {reminderPreview.totalCommissionsToRemind} faturaya mail gönderilecek
                              </span>
                            ) : (
                              "Yükleniyor..."
                            )}
                          </DialogDescription>
                        </DialogHeader>

                        {/* Preview Table */}
                        {isLoadingPreview ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <span className="ml-2">Veriler yükleniyor...</span>
                          </div>
                        ) : reminderPreview && reminderPreview.commissions.length > 0 ? (
                          <div className="my-4">
                            <Label className="text-sm font-medium mb-2 block">
                              Mail Gönderilecek Faturalar
                            </Label>
                            <div className="h-[300px] overflow-auto rounded-md border">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>İsim</TableHead>
                                    <TableHead>E-posta</TableHead>
                                    <TableHead>Tutar</TableHead>
                                    <TableHead>Son Mail Tarihi</TableHead>
                                    <TableHead>Son Mail Tipi</TableHead>
                                    <TableHead>Açılma</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {reminderPreview.commissions.map((commission) => (
                                    <TableRow key={commission.id}>
                                      <TableCell className="font-medium">
                                        {commission.influencerName}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {commission.email}
                                      </TableCell>
                                      <TableCell>
                                        {formatTRY(commission.finalAmount)}
                                      </TableCell>
                                      <TableCell>
                                        {commission.lastEmail ? (
                                          <div className="flex items-center gap-1 text-sm">
                                            <Clock className="h-3 w-3" />
                                            {formatDate(commission.lastEmail.sentAt || commission.lastEmail.createdAt)}
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {commission.lastEmail ? (
                                          <Badge variant="outline" className="text-xs">
                                            {getEmailTypeLabel(commission.lastEmail.type)}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {commission.lastEmail && commission.lastEmail.openCount > 0 ? (
                                          <div className="flex items-center gap-1 text-green-600">
                                            <Eye className="h-3 w-3" />
                                            {commission.lastEmail.openCount}x
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">
                              Toplam: {reminderPreview.commissions.length} fatura
                            </div>
                          </div>
                        ) : reminderPreview && reminderPreview.commissions.length === 0 ? (
                          <div className="py-8 text-center text-muted-foreground">
                            <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Hatırlatma maili gönderilecek fatura bulunamadı.</p>
                            <p className="text-sm">Sadece "Mail Sended" durumundaki faturalara hatırlatma gönderilebilir.</p>
                          </div>
                        ) : null}

                        <div className="grid gap-4 py-4">
                          <div className="grid items-center">
                            <Label htmlFor="mailBodyMessage">Mesaj</Label>
                            <ReactQuill
                              theme="snow"
                              onChange={(value) =>
                                setValue("mailBodyMessage", value)
                              }
                              className="mt-2"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button 
                            type="submit" 
                            disabled={!reminderPreview || reminderPreview.commissions.length === 0}
                          >
                            <Send className="mr-2 h-4 w-4" />
                            Hatırlatma Maili Gönder ({reminderPreview?.totalCommissionsToRemind || 0})
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Commission / Statistics Cards - Sağ */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {statsCards.map((stat, index) => {
                const Icon = stat.icon;
                const isError = stat.color === "red";
                return (
                  <Card
                    key={index}
                    className={`flex flex-col justify-between ${
                      isError ? "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800" : ""
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <CardDescription
                        className={`text-sm font-medium ${
                          isError ? "text-red-700 dark:text-red-400" : ""
                        }`}
                      >
                        {stat.label}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[stat.color]}`}
                        >
                          <Icon className="h-6 w-6" />
                        </div>
                        <CardTitle
                          className={`text-4xl ${
                            isError ? "text-red-700 dark:text-red-400" : ""
                          }`}
                        >
                          {stat.value}
                        </CardTitle>
                      </div>
                    </CardContent>
                    {stat.link && (
                      <CardFooter className="pt-4 border-t">
                        <Button
                          variant={isError ? "destructive" : "outline"}
                          className="w-full"
                          asChild
                        >
                          <Link to={stat.link}>{stat.linkText}</Link>
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* Finansal Özet - En alt */}
        <Card>
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Finansal Detaylar
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {financialData.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="border-l-4 border-l-primary">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[item.color]}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground truncate">
                            {item.label}
                          </p>
                          <p className="text-xl font-bold mt-1">
                            {formatTRY(item.value)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

    </AppLayout>
  );
}
